import React, { useContext, useEffect, useMemo, useState } from "react";
import {
  Dialog,
  DialogActions,
  DialogContent,
  DialogTitle,
  Button,
  Grid,
  TextField,
  FormControlLabel,
  Switch,
  FormControl,
  InputLabel,
  Select,
  MenuItem,
  Typography,
  Paper
} from "@material-ui/core";
import { useHistory } from "react-router-dom";
import api from "../../services/api";
import toastError from "../../errors/toastError";
import { toast } from "react-toastify";
import { AuthContext } from "../../context/Auth/AuthContext";
import BusinessPartnerContactModal from "../BusinessPartnerContactModal";
import ContactModal from "../ContactModal";
import NewTicketModal from "../NewTicketModal";
import ShowTicketOpenModal from "../ShowTicketOpenModal";

const emptyState = {
  name: "",
  document: "",
  email: "",
  phone: "",
  segmentId: "",
  subsegmentId: "",
  economicGroupId: "",
  addressStreet: "",
  addressNumber: "",
  addressComplement: "",
  addressDistrict: "",
  addressCity: "",
  addressState: "",
  addressZipCode: "",
  notes: "",
  active: true
};

const translateRelationshipType = value => {
  if (value === "client") return "Cliente";
  if (value === "agency") return "Agência";
  if (value === "partner") return "Parceiro";
  if (value === "supplier") return "Fornecedor";
  return value || "-";
};

const BusinessPartnerModal = ({ open, onClose, businessPartnerId, onSave }) => {
  const history = useHistory();
  const { user } = useContext(AuthContext);

  const [record, setRecord] = useState(emptyState);
  const [loading, setLoading] = useState(false);
  const [segments, setSegments] = useState([]);
  const [subsegments, setSubsegments] = useState([]);
  const [economicGroups, setEconomicGroups] = useState([]);
  const [relations, setRelations] = useState([]);
  const [contactModalOpen, setContactModalOpen] = useState(false);
  const [editingRelation, setEditingRelation] = useState(null);

  const [editContactModalOpen, setEditContactModalOpen] = useState(false);
  const [editingContactId, setEditingContactId] = useState(null);

  const [newTicketModalOpen, setNewTicketModalOpen] = useState(false);
  const [participantInitialContact, setParticipantInitialContact] = useState(null);
  const [openTicketAlert, setOpenTicketAlert] = useState(false);
  const [openTicketInfo, setOpenTicketInfo] = useState({ user: "", queue: "" });

  const loadRelations = async bpId => {
    if (!bpId) {
      setRelations([]);
      return;
    }

    try {
      const { data } = await api.get("/contact-business-partners", {
        params: { businessPartnerId: bpId }
      });
      setRelations((Array.isArray(data) ? data : []).filter(item => item && typeof item === "object" && item.id != null));
    } catch (err) {
      toastError(err);
    }
  };

  useEffect(() => {
    if (!open) return;

    const loadOptions = async () => {
      try {
        const [segmentsRes, subsegmentsRes, economicGroupsRes] = await Promise.all([
          api.get("/segments"),
          api.get("/subsegments"),
          api.get("/economic-groups")
        ]);

        setSegments((Array.isArray(segmentsRes?.data?.segments) ? segmentsRes.data.segments : []).filter(item => item && typeof item === "object" && item.id != null));
        setSubsegments((Array.isArray(subsegmentsRes?.data?.subsegments) ? subsegmentsRes.data.subsegments : []).filter(item => item && typeof item === "object" && item.id != null));
        setEconomicGroups((Array.isArray(economicGroupsRes?.data?.economicGroups) ? economicGroupsRes.data.economicGroups : []).filter(item => item && typeof item === "object" && item.id != null));
      } catch (err) {
        toastError(err);
      }
    };

    loadOptions();
  }, [open]);

  useEffect(() => {
    if (!open) return;

    if (!businessPartnerId) {
      setRecord(emptyState);
      setRelations([]);
      return;
    }

    const loadRecord = async () => {
      try {
        setLoading(true);
        const { data } = await api.get(`/business-partners/${businessPartnerId}`);
        setRecord({
          name: data.name || "",
          document: data.document || "",
          email: data.email || "",
          phone: data.phone || "",
          segmentId: data.segmentId || "",
          subsegmentId: data.subsegmentId || "",
          economicGroupId: data.economicGroupId || "",
          addressStreet: data.addressStreet || "",
          addressNumber: data.addressNumber || "",
          addressComplement: data.addressComplement || "",
          addressDistrict: data.addressDistrict || "",
          addressCity: data.addressCity || "",
          addressState: data.addressState || "",
          addressZipCode: data.addressZipCode || "",
          notes: data.notes || "",
          active: data.active !== false
        });

        await loadRelations(businessPartnerId);
        setLoading(false);
      } catch (err) {
        setLoading(false);
        toastError(err);
      }
    };

    loadRecord();
  }, [open, businessPartnerId]);

  const handleChange = e => {
    const { name, value } = e.target;

    setRecord(prev => {
      const updated = { ...prev, [name]: value };

      if (name === "segmentId") {
        updated.subsegmentId = "";
      }

      return updated;
    });
  };

  const handleToggle = e => {
    setRecord(prev => ({ ...prev, active: e.target.checked }));
  };

  const handleDeleteRelation = async relationId => {
    try {
      await api.delete(`/contact-business-partners/${relationId}`);
      toast.success("Vínculo removido com sucesso");
      await loadRelations(businessPartnerId);
    } catch (err) {
      toastError(err);
    }
  };

  const handleCloseTicketAlert = () => {
    setOpenTicketAlert(false);
    setOpenTicketInfo({ user: "", queue: "" });
  };

  const handleTicketCreated = ticket => {
    setNewTicketModalOpen(false);

    if (ticket && (ticket.uuid || ticket.id)) {
      const target = ticket.uuid ? `/tickets/${ticket.uuid}` : `/tickets/${ticket.id}`;
      history.push(target);
    }
  };

  const handleStartTicket = async contact => {
    try {
      const contactId = contact?.id;
      if (!contactId) return;

      const ticketsResponse = await api.get("/tickets", {
        params: {
          pageNumber: "1"
        }
      });

      const existingTicket = ticketsResponse.data?.tickets?.find(
        ticket =>
          ticket.contactId === contactId &&
          ["open", "pending", "chatbot"].includes(ticket.status)
      );

      if (existingTicket) {
        if (existingTicket.userId && existingTicket.userId !== user?.id) {
          setOpenTicketInfo({
            user: existingTicket.user?.name || "Outro usuário",
            queue: existingTicket.queue?.name || "Sem fila"
          });
          setOpenTicketAlert(true);
          return;
        } else {
          handleTicketCreated(existingTicket);
          return;
        }
      }

      setParticipantInitialContact({
        id: contact.id,
        name: contact.name,
        number: contact.number,
        email: contact.email || ""
      });
      setNewTicketModalOpen(true);
    } catch (err) {
      toastError(err);
    }
  };

  const handleSave = async () => {
    try {
      setLoading(true);

      if (!record.name || !record.name.trim()) {
        toast.error("Nome é obrigatório");
        setLoading(false);
        return;
      }

      const payload = {
        ...record,
        segmentId: record.segmentId || null,
        subsegmentId: record.subsegmentId || null,
        economicGroupId: record.economicGroupId || null
      };

      if (businessPartnerId) {
        await api.put(`/business-partners/${businessPartnerId}`, payload);
        toast.success("Parceiro de Negócios atualizado com sucesso");
      } else {
        await api.post("/business-partners", payload);
        toast.success("Parceiro de Negócios criado com sucesso");
      }

      setLoading(false);
      onSave && onSave();
      onClose();
    } catch (err) {
      setLoading(false);
      toastError(err);
    }
  };

  const filteredSubsegments = (Array.isArray(subsegments) ? subsegments : [])
    .filter(item => item && typeof item === "object" && item.id != null)
    .filter(item => !record.segmentId || Number(item.segmentId) === Number(record.segmentId));

  const sortedRelations = useMemo(() => {
    return (Array.isArray(relations) ? relations : [])
      .filter(item => item && typeof item === "object" && item.id != null)
      .sort((a, b) => {
        if (!!a.isPrimary === !!b.isPrimary) return Number(b.id) - Number(a.id);
        return a.isPrimary ? -1 : 1;
      });
  }, [relations]);

  if (!open) {
    return null;
  }

  return (
    <>
{contactModalOpen && (
  <BusinessPartnerContactModal
    open={contactModalOpen}
    onClose={() => {
      setContactModalOpen(false);
      setEditingRelation(null);
    }}
    businessPartnerId={businessPartnerId}
    existingRelations={relations}
    editingRelation={editingRelation}
    onSave={() => loadRelations(businessPartnerId)}
  />
)}

{editContactModalOpen && (
  <ContactModal
    open={editContactModalOpen}
    contactId={editingContactId}
    onClose={() => {
      setEditContactModalOpen(false);
      setEditingContactId(null);
    }}
    onSave={() => loadRelations(businessPartnerId)}
  />
)}

{newTicketModalOpen && (
  <NewTicketModal
    modalOpen={newTicketModalOpen}
    onClose={handleTicketCreated}
    initialContact={participantInitialContact}
  />
)}

{openTicketAlert && (
  <ShowTicketOpenModal
    isOpen={openTicketAlert}
    handleClose={handleCloseTicketAlert}
    user={openTicketInfo.user}
    queue={openTicketInfo.queue}
  />
)}


      <Dialog open={open} onClose={onClose} fullWidth maxWidth="md">
        <DialogTitle>
  
        {businessPartnerId ? "Editar Parceiro de Negócios" : "Novo Parceiro de Negócios"}
        </DialogTitle>

        <DialogContent dividers>
          <Grid container spacing={2}>
            <Grid item xs={12} md={6}>
              <TextField
                fullWidth
                label="Nome"
                name="name"
                value={record.name}
                onChange={handleChange}
                disabled={loading}
              />
            </Grid>

            <Grid item xs={12} md={6}>
              <TextField
                fullWidth
                label="Documento"
                name="document"
                value={record.document}
                onChange={handleChange}
                disabled={loading}
              />
            </Grid>

            <Grid item xs={12} md={6}>
              <TextField
                fullWidth
                label="Email"
                name="email"
                value={record.email}
                onChange={handleChange}
                disabled={loading}
              />
            </Grid>

            <Grid item xs={12} md={6}>
              <TextField
                fullWidth
                label="Telefone"
                name="phone"
                value={record.phone}
                onChange={handleChange}
                disabled={loading}
              />
            </Grid>

            <Grid item xs={12} md={4}>
              <FormControl fullWidth>
                <InputLabel>Segmento</InputLabel>
                <Select
                  name="segmentId"
                  value={record.segmentId}
                  onChange={handleChange}
                  disabled={loading}
                >
                  <MenuItem value="">
                    <em>Nenhum</em>
                  </MenuItem>
                  {segments.filter(item => item && typeof item === "object" && item.id != null).map(item => (
                    <MenuItem key={item.id} value={item.id}>
                      {item.name}
                    </MenuItem>
                  ))}
                </Select>
              </FormControl>
            </Grid>

            <Grid item xs={12} md={4}>
              <FormControl fullWidth>
                <InputLabel>Subsegmento</InputLabel>
                <Select
                  name="subsegmentId"
                  value={record.subsegmentId}
                  onChange={handleChange}
                  disabled={loading}
                >
                  <MenuItem value="">
                    <em>Nenhum</em>
                  </MenuItem>
                  {filteredSubsegments.filter(item => item && typeof item === "object" && item.id != null).map(item => (
                    <MenuItem key={item.id} value={item.id}>
                      {item.name}
                    </MenuItem>
                  ))}
                </Select>
              </FormControl>
            </Grid>

            <Grid item xs={12} md={4}>
              <FormControl fullWidth>
                <InputLabel>Grupo Econômico</InputLabel>
                <Select
                  name="economicGroupId"
                  value={record.economicGroupId}
                  onChange={handleChange}
                  disabled={loading}
                >
                  <MenuItem value="">
                    <em>Nenhum</em>
                  </MenuItem>
                  {economicGroups.filter(item => item && typeof item === "object" && item.id != null).map(item => (
                    <MenuItem key={item.id} value={item.id}>
                      {item.name}
                    </MenuItem>
                  ))}
                </Select>
              </FormControl>
            </Grid>

            <Grid item xs={12} md={8}>
              <TextField
                fullWidth
                label="Endereço"
                name="addressStreet"
                value={record.addressStreet}
                onChange={handleChange}
                disabled={loading}
              />
            </Grid>

            <Grid item xs={12} md={4}>
              <TextField
                fullWidth
                label="Número"
                name="addressNumber"
                value={record.addressNumber}
                onChange={handleChange}
                disabled={loading}
              />
            </Grid>

            <Grid item xs={12} md={6}>
              <TextField
                fullWidth
                label="Complemento"
                name="addressComplement"
                value={record.addressComplement}
                onChange={handleChange}
                disabled={loading}
              />
            </Grid>

            <Grid item xs={12} md={6}>
              <TextField
                fullWidth
                label="Bairro"
                name="addressDistrict"
                value={record.addressDistrict}
                onChange={handleChange}
                disabled={loading}
              />
            </Grid>

            <Grid item xs={12} md={5}>
              <TextField
                fullWidth
                label="Cidade"
                name="addressCity"
                value={record.addressCity}
                onChange={handleChange}
                disabled={loading}
              />
            </Grid>

            <Grid item xs={12} md={3}>
              <TextField
                fullWidth
                label="Estado"
                name="addressState"
                value={record.addressState}
                onChange={handleChange}
                disabled={loading}
              />
            </Grid>

            <Grid item xs={12} md={4}>
              <TextField
                fullWidth
                label="CEP"
                name="addressZipCode"
                value={record.addressZipCode}
                onChange={handleChange}
                disabled={loading}
              />
            </Grid>

            <Grid item xs={12}>
              <TextField
                fullWidth
                multiline
                rows={3}
                label="Observações"
                name="notes"
                value={record.notes}
                onChange={handleChange}
                disabled={loading}
              />
            </Grid>

            <Grid item xs={12}>
              <FormControlLabel
                control={
                  <Switch
                    checked={record.active}
                    onChange={handleToggle}
                    color="primary"
                    disabled={loading}
                  />
                }
                label="Ativo"
              />
            </Grid>

            {businessPartnerId && (
              <Grid item xs={12}>
                <Paper style={{ padding: 16, marginTop: 16 }}>
                  <Grid container justifyContent="space-between" alignItems="center" spacing={2}>
                    <Grid item>
                      <Typography variant="h6">
                        Contatos Vinculados ({relations.length})
                      </Typography>
                    </Grid>
                    <Grid item>
                      <Button
                        variant="contained"
                        color="primary"
                        onClick={() => {
                          setEditingRelation(null);
                          setContactModalOpen(true);
                        }}
                      >
                        Vincular Contato
                      </Button>
                    </Grid>
                  </Grid>

                  <div style={{ marginTop: 16 }}>
                    {sortedRelations.length === 0 ? (
                      <Typography variant="body2" color="textSecondary">
                        Nenhum contato vinculado.
                      </Typography>
                    ) : (
                      sortedRelations.filter(item => item && typeof item === "object" && item.id != null).map(item => (
                        <Paper
                          key={item.id}
                          variant="outlined"
                          style={{
                            padding: 12,
                            marginBottom: 10,
                            border: item.isPrimary ? "1px solid #1976d2" : undefined
                          }}
                        >
                          <Grid container spacing={1}>
                            <Grid item xs={12} md={3}>
                              <Typography variant="subtitle2">
                                {item.contact?.name || "Contato"}
                                {item.isPrimary ? " (Principal)" : ""}
                              </Typography>
                              <Typography variant="body2" color="textSecondary">
                                {item.contact?.number || ""}
                              </Typography>
                            </Grid>

                            <Grid item xs={12} md={3}>
                              <Typography variant="body2">
                                <strong>Posição:</strong> {item.position?.name || "-"}
                              </Typography>
                              <Typography variant="body2">
                                <strong>Departamento:</strong> {item.department || "-"}
                              </Typography>
                            </Grid>

                            <Grid item xs={12} md={3}>
                              <Typography variant="body2">
                                <strong>Relação:</strong> {translateRelationshipType(item.relationshipType)}
                              </Typography>
                              <Typography variant="body2">
                                <strong>Status:</strong> {item.active ? "Ativo" : "Inativo"}
                              </Typography>
                            </Grid>

                            <Grid item xs={12} md={3} style={{ textAlign: "right" }}>
                              <Button
                                size="small"
                                color="primary"
                                onClick={() => {
                                  setEditingRelation(item);
                                  setContactModalOpen(true);
                                }}
                              >
                                Editar vínculo
                              </Button>

                              <Button
                                size="small"
                                style={{ marginLeft: 8 }}
                                onClick={() => {
                                  setEditingContactId(item.contactId);
                                  setEditContactModalOpen(true);
                                }}
                              >
                                Editar contato
                              </Button>

                              <Button
                                size="small"
                                style={{ marginLeft: 8 }}
                                onClick={() => handleStartTicket(item.contact)}
                              >
                                Atender
                              </Button>

                              <Button
                                color="secondary"
                                size="small"
                                style={{ marginLeft: 8 }}
                                onClick={() => handleDeleteRelation(item.id)}
                              >
                                Remover
                              </Button>
                            </Grid>
                          </Grid>
                        </Paper>
                      ))
                    )}
                  </div>
                </Paper>
              </Grid>
            )}
          </Grid>
        </DialogContent>

        <DialogActions>
          <Button onClick={onClose} disabled={loading}>
            Cancelar
          </Button>
          <Button color="primary" variant="contained" onClick={handleSave} disabled={loading}>
            Salvar
          </Button>
        </DialogActions>
      </Dialog>
    </>
  );
};

export default BusinessPartnerModal;
